import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { enquiriesApi, type EnquiryListItemDto, type ListEnquiriesParams, type EnquiryStatus } from "@/lib/enquiries";
import { EnquiryStatusBadge } from "@/components/enquiries/enquiry-status-badge";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { IntentButton } from "@/components/intent-button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

// ---------------------------------------------------------------------------
// Filter state
// ---------------------------------------------------------------------------

interface FilterState {
  q: string;
  status: string;
}

const EMPTY_FILTERS: FilterState = {
  q: "",
  status: "",
};

const ENQUIRY_STATUSES: EnquiryStatus[] = [
  "New",
  "InQuote",
  "Quoted",
  "Won",
  "Lost",
  "Abandoned",
];

// ---------------------------------------------------------------------------
// Build API params from filter state
// ---------------------------------------------------------------------------

function buildParams(state: FilterState, pageIndex: number): ListEnquiriesParams {
  return {
    q: state.q || undefined,
    status: (state.status as EnquiryStatus) || undefined,
    page: pageIndex,
    pageSize: 50,
  };
}

// ---------------------------------------------------------------------------
// EnquiriesListPage
// ---------------------------------------------------------------------------

export function EnquiriesListPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [pageIndex, setPageIndex] = useState(0);

  const params = useMemo(() => buildParams(filters, pageIndex), [filters, pageIndex]);

  const { data, isLoading } = useQuery({
    queryKey: ["enquiries", params],
    queryFn: () => enquiriesApi.list(params),
  });

  const visibleRows = useMemo(() => data?.items ?? [], [data]);

  function updateFilters(patch: Partial<FilterState>) {
    setFilters((f) => ({ ...f, ...patch }));
    setPageIndex(0);
  }

  const cols: DataGridColumn<EnquiryListItemDto>[] = [
    {
      id: "id",
      header: "#",
      accessor: (r) => (
        <Link to={`/enquiries/${r.enquiryId}`} className="underline text-primary">
          {r.enquiryId}
        </Link>
      ),
    },
    {
      id: "description",
      header: "Description",
      accessor: (r) => r.description ?? "—",
    },
    {
      id: "client",
      header: "Client",
      accessor: (r) => r.clientName ?? "—",
    },
    {
      id: "owner",
      header: "Owner",
      accessor: (r) => r.ownerEmployeeName ?? "—",
    },
    {
      id: "status",
      header: "Status",
      accessor: (r) => <EnquiryStatusBadge status={r.status} />,
    },
    {
      id: "source",
      header: "Source",
      accessor: (r) => r.sourceSystem,
    },
    {
      id: "created",
      header: "Created",
      accessor: (r) =>
        r.createdOn ? new Date(r.createdOn).toLocaleDateString("en-AU") : "—",
    },
  ];

  return (
    <ListPage
      title="Enquiries"
      primaryAction={
        <IntentButton onClick={() => navigate("/enquiries/new")} aria-label="New enquiry">
          + New Enquiry
        </IntentButton>
      }
      filterBar={
        <div className="space-y-3">
          {/* Status filter pills */}
          <div className="flex flex-wrap gap-1">
            <span className="self-center text-xs text-muted-foreground mr-1">Status:</span>
            <button
              type="button"
              onClick={() => updateFilters({ status: "" })}
              className={`inline-flex h-7 items-center rounded-md px-2 text-xs font-medium border ${
                filters.status === ""
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-background text-foreground border-border hover:bg-accent"
              }`}
            >
              All
            </button>
            {ENQUIRY_STATUSES.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => updateFilters({ status: s })}
                aria-pressed={filters.status === s}
                className={`inline-flex h-7 items-center rounded-md px-2 text-xs font-medium border ${
                  filters.status === s
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background text-foreground border-border hover:bg-accent"
                }`}
              >
                {s}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <Label htmlFor="enquiries-q">Search description, external ID</Label>
              <Input
                id="enquiries-q"
                value={filters.q}
                onChange={(e) => updateFilters({ q: e.target.value })}
                placeholder="Search…"
                aria-label="Search enquiries"
              />
            </div>
          </div>
        </div>
      }
    >
      <DataGrid
        rows={visibleRows}
        columns={cols}
        rowKey={(r) => r.enquiryId}
        totalRows={data?.totalCount ?? 0}
        pageIndex={pageIndex}
        pageSize={50}
        loading={isLoading}
        emptyHeadline="No enquiries match these filters."
        onPageChange={setPageIndex}
      />
    </ListPage>
  );
}
